import {
  createAnalyticsMiddleware,
  type ReduxEventMapping,
} from '@center-technologies/analytics';

/**
 * Redux event mappings for the Center-PWA store. Lives in the store package
 * (rather than in app/utils/analyticsConfig.ts) because store.ts has to
 * register the middleware at configureStore time and crossing the
 * store→app package boundary trips Nx's module-boundary rule.
 *
 * Mappings reference RTK action types as strings — no need to import
 * thunks, so this stays decoupled from the reducer implementations.
 *
 * Add new mappings as Redux thunks land. For axios-driven events
 * (sphere_created, approach_deployed, etc.) see analyticsConfig.ts in
 * the app package.
 */

const obj = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
const str = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;
const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined;
const arrayLength = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

/** The fields the arc-template events read off a fulfilled thunk's `ArcTemplateRow` payload. */
const arcTemplateRow = (payload: unknown) => {
  const row = obj(payload);
  const definition = obj(row?.definition);
  const stages = Array.isArray(definition?.stages)
    ? definition.stages.map((stage) => obj(stage) ?? {})
    : [];
  return { systemName: str(row?.system_name), version: num(row?.version), stages };
};

const MAPPINGS: ReduxEventMapping[] = [
  // Contexts — context_created is fired directly in components (includes entryPath/templateRef)
  {
    type: 'contexts/updateContextThunk/fulfilled',
    event: 'context_updated',
  },
  {
    type: 'contexts/deleteContextThunk/fulfilled',
    event: 'context_deleted',
  },
  {
    type: 'contexts/cloneContextThunk/fulfilled',
    event: 'context_cloned',
  },

  // Arc templates (EAINT-951) — create/save/publish fire straight off the thunk's own
  // fulfilled action; the payload is the saved `ArcTemplateRow`, so every field the spec's
  // analytics table names (systemName, version, stage/intent/action counts) is read from it.
  // Creation always lands an empty definition (spec D23), so the starter is reported by
  // `arc_editor_block_added { via: 'starter' }` at the moment it is chosen, not here.
  {
    type: 'arcTemplates/createArcTemplate/fulfilled',
    event: 'arc_template_created',
    extract: (payload) => {
      const { systemName, stages } = arcTemplateRow(payload);
      return { systemName, stageCount: stages.length };
    },
  },
  {
    type: 'arcTemplates/saveArcTemplate/fulfilled',
    event: 'arc_template_saved',
    extract: (payload) => {
      const { systemName, version, stages } = arcTemplateRow(payload);
      return {
        systemName,
        version,
        stageCount: stages.length,
        intentCount: stages.reduce((sum, stage) => sum + arrayLength(stage.intents), 0),
        actionCount: stages.reduce(
          (sum, stage) => sum + arrayLength(stage.onEnter) + arrayLength(stage.onExit),
          0
        ),
      };
    },
  },
  {
    type: 'arcTemplates/publishArcTemplate/fulfilled',
    event: 'arc_template_published',
    extract: (payload) => {
      const { systemName, version } = arcTemplateRow(payload);
      return { systemName, version };
    },
  },

  // Notifications
  {
    type: 'notifications/markNotificationAsRead/fulfilled',
    event: 'notification_read',
  },

  // Session boot
  {
    type: 'userProfile/fetchIdentity/fulfilled',
    event: 'session_identity_loaded',
  },
];

export const storeAnalyticsMiddleware = createAnalyticsMiddleware(MAPPINGS);
